// v0.5.0f — Inspect Drawer (redesigned). Replaces the v0.5.0d
// drawer in full. Spec: INSPECT_DRAWER_REDESIGN_WALKTHROUGHS.md.
//
// Sub-components (broken out to stay under the 500-LOC frontend
// cap):
//   - <InspectDecisionA>      Walkthrough A — single-txn case.
//   - <InspectDecisionB>      Walkthrough B — systemic case.
//   - <InspectKL1Panel>       Disabled "DATA MISTAKE" + KL-1 panel.
//   - <InspectReasonPreview>  Server-generated reason + edit toggle.
//
// No preset dropdowns. No <TOKEN> placeholders. The reason text
// is fetched from `/calc/{id}/inspect-reason` after the operator
// picks a decision, then rendered read-only by default with an
// optional "Edit the audit reason before confirming" toggle.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { errorMessage } from "../../lib/errorMessage";
import {
  getInspectReason,
} from "../../lib/api";
import { useGuardedSupersede } from "../../workflow/useGuardedSupersede";
import {
  countrySupportsManualOverride, manualOverrideBoxFor,
  noOverrideReasonFor,
} from "../../lib/manualOverrideConfig";
import { InspectDecisionA } from "./InspectDecisionA";
import { InspectDecisionB } from "./InspectDecisionB";
import { InspectReasonPreview } from "./InspectReasonPreview";
import { labelFor as enumLabel } from "../../lib/enumLabels";

const fmtPct = (rate) => {
  const n = Number(rate);
  if (!Number.isFinite(n)) return rate;
  return `${(n * 100).toFixed(n * 100 % 1 === 0 ? 0 : 2)}%`;
};

const fmtMoney = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

const fmtHumanDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  });
};

const adapterTitle = (a) => {
  const k = (a || "").toLowerCase();
  return ({
    client_template: "Client template",
  })[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1) : "unknown");
};

export const OffenderInspectDrawer = ({
  open, onOpenChange,
  calcId, country, offender, periodStart, periodEnd,
  txns, aggregate, loading, err,
  flagged, note, setNote, noteValid, setNoteValid,
  flagBusy, flagErr, onFlag, existingFlag,
  navigate,
}) => {
  const { t } = useTranslation();
  // A1-93 — routing to the manual box supersedes the calc: same plan as
  // every other supersede (the form's corrected rates travel with it).
  const { routeManualOverrideAsync: guardedRoute } = useGuardedSupersede(calcId);
  // Suppress unused-prop lint — these props are kept for
  // back-compat with OffenderActions.jsx (it still owns the
  // `note`/`noteValid` state and passes them through; the
  // v0.5.0f drawer fills `note` with the server-generated reason
  // via setNote() before calling onFlag).
  void noteValid; void setNoteValid; void note;
  const [decision, setDecision] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [reasonLoading, setReasonLoading] = useState(false);
  const [reasonErr, setReasonErr] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmErr, setConfirmErr] = useState(null);
  // v0.6.10 — live phase from the background job; a frozen button
  // on a multi-minute destructive action gets clicked twice.
  const [confirmPhase, setConfirmPhase] = useState(null);

  const cc = (country || "").toUpperCase();
  const supportsRoute = countrySupportsManualOverride(cc);
  const manualBoxId = manualOverrideBoxFor(cc);
  const noOverrideReason = noOverrideReasonFor(cc);
  const looksSystemic = !!aggregate?.looks_systemic;
  const txnCount = (txns || []).length;
  const txnIds = (txns || []).map((t) => t.id).filter(Boolean);
  const txn0 = (txns || [])[0] || {};

  useEffect(() => {
    if (!open) {
      setDecision(null);
      setReasonText("");
      setReasonErr(null);
      setConfirmErr(null);
    }
  }, [open, offender?.rate]);

  const pickDecision = async (next) => {
    setDecision(next);
    setReasonErr(null);
    setConfirmErr(null);
    setReasonText("");
    setReasonLoading(true);
    try {
      const data = await getInspectReason(calcId, {
        country: cc, rate: String(offender.rate), decision: next,
      });
      setReasonText(data?.reason || "");
    } catch (e) {
      setReasonErr(errorMessage(e));
    } finally {
      setReasonLoading(false);
    }
  };

  const onConfirmRoute = async () => {
    if (!manualBoxId) return;
    if (confirmBusy) return;
    setConfirmBusy(true); setConfirmErr(null); setConfirmPhase("starting");
    try {
      const data = await guardedRoute({
        txn_ids: txnIds,
        box_id: manualBoxId,
        reason: reasonText.trim(),
      }, (job) => setConfirmPhase(
        job?.progress?.phase || job?.status || null,
      ));
      onOpenChange(false);
      if (navigate && data?.new_calc_id) {
        navigate(`/?calc_id=${data.new_calc_id}`);
      } else if (data?.new_calc_id) {
        window.location.assign(`/?calc_id=${data.new_calc_id}`);
      }
    } catch (e) {
      setConfirmErr(errorMessage(e));
    } finally {
      setConfirmBusy(false);
      setConfirmPhase(null);
    }
  };

  const onConfirmFlag = async () => {
    // v0.5.0f bugfix — must pass the server-generated reason
    // EXPLICITLY because React state updates are async; calling
    // setNote(reasonText) then immediately awaiting onFlag()
    // posts the STALE `note` from the closure (null/empty).
    await onFlag(reasonText);
  };

  // v0.6.12 (A2-181) — subtotal PER CURRENCY. These amounts are in each
  // transaction's own currency, so one sum across them adds pounds to
  // euros and presents the result as a single figure. The operator's own
  // run was 39 GBP rows and 1 EUR against a euro return, so the group
  // total it would have shown was meaningless — and it was rendered with
  // a euro sign, which made it look authoritative.
  //
  // Converting here was the alternative and was rejected: the drawer has
  // no access to the calc's rate table, and inventing a conversion in a
  // display component is how a figure nobody can trace gets onto a
  // screen. Stating the subtotals is honest and needs nothing it does
  // not already hold.
  const byCurrency = (txns || []).reduce((acc, t) => {
    const cur = t.currency || "?";
    const e = acc[cur] || { net: 0, vat: 0 };
    e.net += Number(t.net_amount || 0);
    e.vat += Number(t.vat_amount || 0);
    acc[cur] = e;
    return acc;
  }, {});
  const currencies = Object.keys(byCurrency).sort();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid={`offender-drawer-${offender.rate}`}
      >
        <SheetHeader>
          <SheetTitle>
            {t("ui.country_return.inspect_drawer.title", { cc, rate: fmtPct(offender.rate), count: txnCount, defaultValue: `Row review — ${cc} · rate ${fmtPct(offender.rate)} · ${txnCount} transaction${txnCount === 1 ? "" : "s"}` })}
          </SheetTitle>
          <SheetDescription>
            {txnCount === 1 ? (
              <>
                {t("ui.country_return.inspect_drawer.order_hash", { defaultValue: "Order #" })}{String(txn0.order_id || "").replace(/^#+/, "") || txn0.id} · {adapterTitle(txn0.adapter)} ·
                {" "}{fmtHumanDate(txn0.transaction_date)} ·
                {/* v0.6.12 (A2-181) — the amount is in the TRANSACTION'S
                    currency, not the filing's. A hardcoded euro sign here
                    showed "€2,050.00" for a row that is 2,050 GBP, while
                    the flagged-rows table above showed 2,366.36, its
                    converted euro figure — the same transaction, two
                    numbers, both wearing a euro sign. */}
                {" "}{fmtMoney(txn0.net_amount)} {txn0.currency} {t("ui.country_return.inspect_drawer.net_dot", { defaultValue: "net ·" })}
                {" "}{fmtMoney(txn0.vat_amount)} {txn0.currency} VAT
              </>
            ) : (
              <>
                {t("ui.country_return.inspect_drawer.no_line", { start: periodStart, end: periodEnd, cc, rate: fmtPct(offender.rate), defaultValue: `Period ${periodStart} → ${periodEnd}. ${cc}'s VAT return doesn't have a line for ${fmtPct(offender.rate)} — this rate is blocking the filing.` })}
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {looksSystemic && (
          <div
            data-testid={`inspect-systemic-lede-${offender.rate}`}
            className="mt-4 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:text-amber-100"
          >
            <div className="font-bold mb-1">
              {t("ui.country_return.inspect_drawer.systemic_title", { n: txnCount, defaultValue: `⚠ This pattern doesn't look like ${txnCount} unrelated errors.` })}
            </div>
            <div>
              {t("ui.country_return.inspect_drawer.systemic_from", { n: txnCount, defaultValue: `All ${txnCount} transactions come from` })}{" "}
              <span className="font-semibold">{adapterTitle(aggregate?.adapter_dominant)}</span>
              {" "}{t("ui.country_return.inspect_drawer.systemic_pct", { pct: aggregate?.adapter_dominant_pct ?? "", defaultValue: `(${aggregate?.adapter_dominant_pct ?? ""}%), landed in a` })}{" "}
              <span className="font-semibold">{t("ui.country_return.inspect_drawer.systemic_window", { days: aggregate?.date_range?.span_days ?? "", defaultValue: `${aggregate?.date_range?.span_days ?? ""}-day window` })}</span>
              {t("ui.country_return.inspect_drawer.same_rate", { defaultValue: ", and all carry the same " })}
              <span className="font-semibold">{fmtPct(offender.rate)}</span>
              {" "}{t("ui.country_return.inspect_drawer.systemic_tail", { cc, n: txnCount, defaultValue: `rate ${cc} doesn't recognise. This is the signature of a source-config change or a real new jurisdictional rate — not ${txnCount} individual data errors.` })}
            </div>
            <div className="mt-2 font-semibold">
              {t("ui.country_return.inspect_drawer.total_at_risk", { defaultValue: "Total at risk this filing:" })}{" "}
              {currencies.map((cur, i) => (
                <span key={cur}>
                  {i > 0 ? " · " : ""}
                  {fmtMoney(byCurrency[cur].net)} {cur} {t("ui.country_return.inspect_drawer.net_dot", { defaultValue: "net ·" })}
                  {" "}{fmtMoney(byCurrency[cur].vat)} {cur} VAT
                </span>
              ))}
            </div>
          </div>
        )}

        {!looksSystemic && !supportsRoute && noOverrideReason && (
          <div
            data-testid={`inspect-no-box-note-${cc}`}
            className="mt-4 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-[11px] leading-snug text-slate-700 dark:text-slate-200"
          >
            <span className="font-bold">{t("ui.country_return.inspect_drawer.no_box", { cc, defaultValue: `${cc}: this VAT return has no manual-other-rates box.` })}</span>{" "}
            {noOverrideReason}
          </div>
        )}

        {!loading && !err && txnCount > 0 && (
          looksSystemic ? (
            <InspectDecisionB
              offender={offender} country={cc} aggregate={aggregate}
              txns={txns} supportsRoute={supportsRoute}
              manualBoxId={manualBoxId} pickDecision={pickDecision}
              flagged={flagged} flagBusy={flagBusy}
            />
          ) : (
            <InspectDecisionA
              offender={offender} country={cc}
              supportsRoute={supportsRoute} manualBoxId={manualBoxId}
              txn0={txn0} pickDecision={pickDecision}
              flagged={flagged} flagBusy={flagBusy}
              calcId={calcId} txnIds={txnIds} txnCount={txnCount}
            />
          )
        )}

        <InspectReasonPreview
          decision={decision} offender={offender}
          country={cc} manualBoxId={manualBoxId} txnCount={txnCount}
          reasonText={reasonText}
          reasonLoading={reasonLoading} reasonErr={reasonErr}
          confirmBusy={confirmBusy} confirmErr={confirmErr}
          confirmPhase={confirmPhase}
          flagged={flagged} flagBusy={flagBusy}
          onConfirmRoute={onConfirmRoute} onConfirmFlag={onConfirmFlag}
        />

        {flagged && (
          <div
            data-testid={`offender-flag-confirmed-${offender.rate}`}
            className="text-xs pt-4 mt-4 border-t border-slate-200 dark:border-slate-700 space-y-2"
          >
            <div className="text-emerald-700 dark:text-emerald-300 font-semibold">
              {t("ui.country_return.offenders.reported_title", { defaultValue: "✓ Rate reported — queued for maintainer review." })}
            </div>
            <div className="text-slate-700 dark:text-slate-200 leading-snug">
              {t("ui.country_return.offenders.reported_p1", { defaultValue: "Your report is" })}{" "}
              <span className="font-semibold">{t("ui.country_return.offenders.reported_s1", { defaultValue: "permanent" })}</span>
              {t("ui.country_return.offenders.reported_p2", { defaultValue: ". This calculation will" })}{" "}
              <span className="font-semibold">{t("ui.country_return.offenders.reported_s2", { defaultValue: "stay blocked" })}</span>{" "}
              {t("ui.country_return.offenders.reported_p3", {
                country: country || "??",
                defaultValue: `until the maintainer updates the engine's ${country || "??"} rate table and you re-run the calculation — that's the compliance guardrail (one operator can't change the rate table on their own).`,
              })}
            </div>
            {existingFlag && (
              <div className="text-[10px] text-slate-500 dark:text-slate-400">
                {t("ui.country_return.offenders.reported_on", { defaultValue: "reported" })} {(existingFlag.flagged_at || "").slice(0, 16).replace("T", " ")}
                {existingFlag.flagged_by ? t("ui.country_return.offenders.by_name", { name: existingFlag.flagged_by, defaultValue: ` by ${existingFlag.flagged_by}` }) : ""}
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-4">
            {t("ui.country_return.inspect_drawer.loading", { defaultValue: "Loading transactions…" })}
          </div>
        )}
        {err && (
          <div
            data-testid={`offender-drawer-error-${offender.rate}`}
            className="text-xs text-rose-700 dark:text-rose-300 mt-4"
          >
            {err}
          </div>
        )}
        {flagErr && (
          <div
            data-testid={`offender-flag-inline-error-${offender.rate}`}
            className="text-[11px] text-rose-700 dark:text-rose-300 mt-2"
          >
            {flagErr}
          </div>
        )}

        {txnCount > 1 && (
          <details
            data-testid={`inspect-txn-table-toggle-${offender.rate}`}
            className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700"
          >
            <summary className="text-xs cursor-pointer text-slate-600 dark:text-slate-300">
              {t("ui.country_return.inspect_drawer.show_all", { n: txnCount, defaultValue: `Show all ${txnCount} transactions ▾` })}
            </summary>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-xs">
                <thead className="text-slate-500 dark:text-slate-400 text-left">
                  <tr>
                    <th className="py-1 pr-2">{t("ui.country_return.inspect_drawer.col_order", { defaultValue: "Order" })}</th>
                    <th className="py-1 pr-2">{t("ui.country_return.inspect_drawer.col_source", { defaultValue: "Source" })}</th>
                    <th className="py-1 pr-2">{t("ui.country_return.inspect_drawer.col_date", { defaultValue: "Date" })}</th>
                    <th className="py-1 pr-2 text-right">{t("ui.country_return.inspect_drawer.col_net", { defaultValue: "Net" })}</th>
                    <th className="py-1 pr-2 text-right">VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {(txns || []).map((t) => (
                    <tr
                      key={t.id || t.order_id}
                      data-testid={`offender-txn-${t.id || t.order_id}`}
                      className="border-t border-slate-200 dark:border-slate-700"
                    >
                      <td className="py-1 pr-2 tabular-nums">{t.order_id}</td>
                      <td className="py-1 pr-2">{enumLabel("adapter", t.adapter)}</td>
                      <td className="py-1 pr-2 tabular-nums">
                        {(t.transaction_date || "").slice(0, 10)}
                      </td>
                      <td className="py-1 pr-2 tabular-nums text-right">
                        {fmtMoney(t.net_amount)} {t.currency}
                      </td>
                      <td className="py-1 pr-2 tabular-nums text-right">
                        {fmtMoney(t.vat_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default OffenderInspectDrawer;
